// src/services/softPushPrompt.js
//
// Contextual "soft ask" for native push notifications. Instead of firing the OS
// permission prompt at signup (which Apple discourages and users tend to deny),
// we show a friendly in-app explainer AFTER a meaningful action (listing a
// pedal, adding a want, making an offer, opening an offer that is waiting on
// you) and only then, if the user opts in, trigger the real OS prompt.
// Entirely native-gated: no-op on web.
//
// Rules (2026-10-07, after a reinstalled iPhone went weeks with no prompt and
// no pushes; see services/pushNotifications.js):
// - AUTOMATIC pop-up only when the installed plugin reports 'prompt' and no
//   snooze is active. Never for 'granted', 'denied', 'prompt-with-rationale'
//   (Android 13+, refused once), an unknown value, or a failed check. A
//   'prompt-with-rationale' user can still turn notifications on from Profile.
// - The device's real permission decides. The old stored 'accepted' note no
//   longer blocks asking: it trapped users whose OS prompt never appeared.
// - SNOOZE (7 days) after "Not now" AND after any permission request that does
//   not end in granted, so a refusal or a prompt that never appeared can't
//   turn into a pop-up on every action. Explicit Profile actions ignore it.
// - ONE AT A TIME: from the permission check until the pop-up has closed and
//   any permission attempt has finished, further triggers are ignored.
//
// Flow:
//   meaningful action → maybePromptForPush(reason)
//     → resolves { opened: false } or { opened: true, closed: Promise }
//   modal "Turn on"  → acceptSoftPushPrompt() → OS prompt via registerPushNotifications
//   modal "Not now"  → dismissSoftPushPrompt() → snooze for SNOOZE_DAYS

import { Capacitor } from '@capacitor/core';
import { registerPushNotifications, getPushPermissionState, REGISTER_RESULTS } from './pushNotifications';

const STORAGE_KEY = 'pxc_push_soft_prompt_v1';
const SNOOZE_DAYS = 7;
export const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000;

// The mounted modal registers itself here so any action handler can trigger it.
let listener = null;
// True from the permission check until the pop-up has fully closed.
let active = false;

export function subscribeToSoftPushPrompt(fn) {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

function writeState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    // best-effort; private mode / quota can throw
  }
}

function isSnoozed(state, now = Date.now()) {
  if (!state || state.status !== 'dismissed' || typeof state.at !== 'number') return false;
  const age = now - state.at;
  return age >= 0 && age < SNOOZE_MS;
}

const NOT_OPENED = Object.freeze({ opened: false });

// Call after a meaningful action. Resolves (never rejects) with whether THIS
// call opened the pop-up and, if so, a promise that settles when it closes.
export async function maybePromptForPush(reason) {
  if (!Capacitor.isNativePlatform()) return NOT_OPENED;
  if (active) return NOT_OPENED;
  active = true;
  let handedToModal = false;
  try {
    const permission = await getPushPermissionState();

    if (permission === 'granted') {
      // Nothing to ask; make sure this device is registered for the session.
      registerPushNotifications({ promptIfNeeded: false });
      return NOT_OPENED;
    }
    if (permission !== 'prompt') return NOT_OPENED;
    if (isSnoozed(readState())) return NOT_OPENED;
    if (!listener) return NOT_OPENED;

    let resolveClosed;
    const closed = new Promise((r) => { resolveClosed = r; });
    // Per opening: a late or repeated call can never release a NEWER
    // pop-up's lock.
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      active = false;
      resolveClosed();
    };
    handedToModal = true;
    listener({ reason, finish });
    return { opened: true, closed };
  } catch (err) {
    return NOT_OPENED;
  } finally {
    if (!handedToModal) active = false;
  }
}

// Shared by the pop-up's "Turn on" and Profile's explicit button: request the
// OS permission (when the plugin allows it) and register. Snoozes automatic
// pop-ups unless the request ends in granted. Never rejects.
export async function requestPushPermission() {
  let result;
  try {
    result = await registerPushNotifications({ promptIfNeeded: true });
  } catch (err) {
    result = REGISTER_RESULTS.PERMISSION_FAILED; // registration never rejects; be conservative
  }
  // Snooze unless it ended in granted: refused, the OS dialog never
  // appeared, or the check/request threw and permission is unknown.
  if (result === REGISTER_RESULTS.PERMISSION_NOT_GRANTED || result === REGISTER_RESULTS.PERMISSION_FAILED) {
    writeState({ status: 'dismissed', at: Date.now() });
  } else if (result === REGISTER_RESULTS.REGISTERED) {
    writeState({ status: 'accepted', at: Date.now() });
  }
  return result;
}

// User tapped "Turn on notifications" in the soft modal → fire the OS prompt.
export async function acceptSoftPushPrompt() {
  return requestPushPermission();
}

// User tapped "Not now" → snooze so we don't nag on the next action.
export function dismissSoftPushPrompt() {
  writeState({ status: 'dismissed', at: Date.now() });
}

export function __resetSoftPromptForTests() {
  active = false;
  listener = null;
}
